import React, { useEffect, useMemo, useState } from "react";
import {
  getMerchantRolePermissions,
  saveMerchantRolePermissions,
} from "../../api/merchantRolePermissionApi";
import ModuleHeader, { ModuleHeaderAction } from "../../components/ModuleHeader/ModuleHeader";
import ModulePageShell from "../../components/ModulePageShell/ModulePageShell";
import "./role-management.scss";

export default function RoleManagementPage() {
  const [selectedRole, setSelectedRole] = useState("Operator");
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [ownerPermissions, setOwnerPermissions] = useState([]);
  const [operatorPermissions, setOperatorPermissions] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    getMerchantRolePermissions()
      .then((data) => {
        setPermissionGroups(Object.entries(data.groups || {}).map(([section, permissions]) => ({
          section,
          permissions: Object.entries(permissions).map(([key, label]) => ({ key, label })),
        })));
        setOwnerPermissions(data.owner_permissions || []);
        setOperatorPermissions(data.operator_permissions || []);
        setCanManage(Boolean(data.can_manage));
      })
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  }, []);

  const selectedPermissions = useMemo(
    () => selectedRole === "Owner" ? ownerPermissions : operatorPermissions,
    [selectedRole, ownerPermissions, operatorPermissions]
  );

  const togglePermission = (permission) => {
    if (selectedRole === "Owner" || !canManage) return;
    setMessage("");
    setOperatorPermissions((current) => current.includes(permission)
      ? current.filter((item) => item !== permission)
      : [...current, permission]);
  };

  const savePermissions = async () => {
    setSaving(true);
    setMessage("");
    setError("");
    try {
      const data = await saveMerchantRolePermissions(operatorPermissions);
      setOperatorPermissions(data.operator_permissions || []);
      setMessage("Operator permissions updated successfully.");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModulePageShell className="role-management">
      <ModuleHeader
        bleed
        title="Roles & Permissions"
        description="Control which portal functions each staff role can access."
        actions={
          canManage ? (
            <ModuleHeaderAction
              onClick={savePermissions}
              disabled={selectedRole === "Owner" || saving || loading}
            >
              {saving ? "Saving..." : "Save Permissions"}
            </ModuleHeaderAction>
          ) : null
        }
      />

      <section className="role-management__layout">
        <aside className="role-management__roles">
          <div className="role-management__roles-header"><h3>User Roles</h3><p>Two roles are available for the MVP.</p></div>
          <button type="button" className={selectedRole === "Owner" ? "is-active" : ""} onClick={() => { setSelectedRole("Owner"); setMessage(""); }}><strong>Owner</strong><span>Created by platform admin</span></button>
          <button type="button" className={selectedRole === "Operator" ? "is-active" : ""} onClick={() => { setSelectedRole("Operator"); setMessage(""); }}><strong>Operator</strong><span>Created and managed by Owner</span></button>
        </aside>

        <div className="role-management__permissions">
          <div className="role-management__permissions-header">
            <div><h3>{selectedRole} Permissions</h3><p>{selectedRole === "Owner" ? "The Owner has full merchant portal access. These permissions are fixed." : "Select the functions that Operator accounts are allowed to access."}</p></div>
            <span>{selectedPermissions.length} Permissions</span>
          </div>

          {error ? <div className="role-management__notice"><strong>Unable to load permissions</strong><span>{error}</span></div> : null}
          {selectedRole === "Owner" ? <div className="role-management__notice"><strong>Protected Owner role</strong><span>The Owner account and core permissions are controlled by platform admin and cannot be removed.</span></div> : null}
          {!canManage && selectedRole === "Operator" ? <div className="role-management__notice"><strong>Read-only access</strong><span>Only a Merchant Owner can change Operator permissions.</span></div> : null}

          <div className="role-management__groups">
            {permissionGroups.map((group) => (
              <section className="role-management__group" key={group.section}>
                <h4>{group.section}</h4>
                <div className="role-management__permission-list">
                  {group.permissions.map((permission) => {
                    const enabled = selectedPermissions.includes(permission.key);
                    const locked = selectedRole === "Owner" || !canManage;
                    return (
                      <label key={permission.key} className={locked ? "is-locked" : ""}>
                        <span><strong>{permission.label}</strong><small>{enabled ? "Allowed" : "Not allowed"}</small></span>
                        <input type="checkbox" checked={enabled} disabled={locked} onChange={() => togglePermission(permission.key)} />
                      </label>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>

          {loading ? <div className="role-management__success">Loading permissions...</div> : null}
          {message ? <div className="role-management__success">{message}</div> : null}
        </div>
      </section>
    </ModulePageShell>
  );
}
